import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import {
  CommonDrawerStyle,
  DrawerForm,
  DrawerHeader,
} from "../../Common/styles/drawer";
import { ModalDrawerButtonStyles } from "../../Common/styles/modal";
import {
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../../Common/styles/table";
import { debounce } from "lodash";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { clearParticipantIds } from "../../../Redux/reducers/participantsSlice";
import { DateTime } from "luxon";

type Props = {
  closeDrawer: () => void;
  formType: "consent" | "sunshine";
};

const SendMultipleConsentDrawer: React.FC<Props> = ({
  closeDrawer,
  formType,
}) => {
  const { id: studyId } = useParams();
  const dispatch = useAppDispatch();

  const accessibleParticipants = useAppSelector(
    (state) => state.participants.accessibleParticipants
  );

  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [consents, setConsents] = useState<any[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [selectedConsents, setSelectedConsents] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const endDate = DateTime.now().toUTC().toISO();
        let prefix = `/admin/study/${studyId}/econsent`;
        if (formType === "sunshine") {
          prefix = `/admin/study/${studyId}/sunshine`;
        }

        let url = `${prefix}?status=active&endDate=${endDate}&page=1&size=20`;
        if (searchText) {
          url += `&q=${searchText}`;
        }

        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data?.data;
        const newData = data?.map((item: any) => ({
          id: item?.latestVersion?.id,
          consentId: item?.consentId,
          name: item?.name,
          version: item?.latestVersion?.version,
        }));

        setConsents(newData || []);

        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchData();
  }, [studyId, searchText, formType]);

  async function submitHandler() {
    try {
      if (selectedConsents.length > 0) {
        setSubmitLoader(true);
        let body = {
          versionIds: [...selectedConsents.map((c) => c.id)],
          participantIds: accessibleParticipants,
        };
        let prefix = `/admin/study/${studyId}/econsent`;
        if (formType === "sunshine") {
          prefix = `/admin/study/${studyId}/sunshine`;
        }
        let res: AxiosResponse;
        res = await http.post(`${prefix}/participants/bulk-send`, body);
        toastMessage("success", res.data.message);
        dispatch(clearParticipantIds());
        closeDrawer();
        setSubmitLoader(false);
      } else {
        toastMessage("warning", "Select atleast one consent to send");
      }
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  }

  const modifySearchTerm = useMemo(
    () =>
      debounce((val) => {
        setSearchText(val);
      }, 500),
    [setSearchText]
  );

  return (
    <Box sx={{ ...CommonDrawerStyle, height: "calc(100vh - 200px)" }}>
      <DrawerHeader
        closeDrawer={closeDrawer}
        title={`Send ${formType === "consent" ? "Consent" : "Sunshine Form"}`}
      />
      <Box
        sx={{
          height: "64px",
          display: "flex",
          alignItems: "center",
          mb: 2,
          width: "50%",
        }}
      >
        <TextField
          placeholder={`Search for ${
            formType === "consent" ? "Consents" : "Forms"
          }`}
          style={{ width: "100%" }}
          defaultValue={searchText}
          onChange={(e) => modifySearchTerm(e.target.value)}
        />
      </Box>

      <Box style={{ height: "100%" }}>
        <>
          <Box sx={{ ...DrawerForm }}>
            <Box sx={{ height: "80%", overflow: "auto" }}>
              <Table
                sx={{
                  ...TableBorderRadiusTopLeftRight,
                }}
              >
                <TableHead>
                  <TableRow>
                    <StyledTableCell>
                      <FormControlLabel
                        label={
                          <Typography fontWeight={"bold"} variant="body2">
                            {formType === "consent"
                              ? "Consent Name"
                              : "Form Name"}
                          </Typography>
                        }
                        control={
                          <Checkbox
                            checked={false}
                            onChange={(event) => {
                              if (event.target.checked) {
                                const newSelected = [...selectedConsents];
                                consents.forEach((c) => {
                                  const found = newSelected.find(
                                    (s) => s.id === c.id
                                  );
                                  if (!found) {
                                    newSelected.push({
                                      id: c.id,
                                      name: c.name + " " + c.version,
                                    });
                                  }
                                });
                                setSelectedConsents(newSelected);
                              }
                            }}
                          />
                        }
                      />
                    </StyledTableCell>
                    <StyledTableCell>Version</StyledTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 1,
                        mt: 2,
                      }}
                    >
                      <CircularProgress size={25} />
                    </Box>
                  ) : consents.length > 0 ? (
                    consents.map((consent) => {
                      const checked = !!selectedConsents.find(
                        (c) => c.id === consent.id
                      );
                      return (
                        <TableRow key={consent.id}>
                          <StyledTableCell>
                            <FormControlLabel
                              label={
                                <Typography fontWeight={"regular"}>
                                  {consent.name}
                                </Typography>
                              }
                              control={
                                <Checkbox
                                  checked={checked}
                                  onChange={(event) => {
                                    let newSelected = [...selectedConsents];
                                    if (event.target.checked) {
                                      newSelected.push({
                                        id: consent.id,
                                        name:
                                          consent.name + " " + consent.version,
                                      });
                                    } else {
                                      newSelected = newSelected.filter(
                                        (c) => c.id !== consent.id
                                      );
                                    }
                                    setSelectedConsents(newSelected);
                                  }}
                                />
                              }
                            />
                          </StyledTableCell>

                          <StyledTableCell>{consent.version}</StyledTableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <Typography textAlign="center" sx={{ mt: 2 }}>
                      No {formType === "consent" ? "consents" : "forms"}{" "}
                      available
                    </Typography>
                  )}
                </TableBody>
              </Table>
            </Box>

            {selectedConsents.length > 0 && (
              <Box
                sx={{
                  marginTop: 2,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                {selectedConsents.map((consent) => {
                  return (
                    <Chip
                      key={consent.id}
                      label={consent.name}
                      onDelete={() => {
                        const newSelected = selectedConsents.filter(
                          (c) => c.id !== consent.id
                        );
                        setSelectedConsents(newSelected);
                      }}
                    />
                  );
                })}
              </Box>
            )}
          </Box>

          <Box sx={{ mt: "auto", pb: 1 }}>
            <Divider />
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textAlign: "center" }}
              >
                {formType === "consent" ? "consent(s)" : "form(s)"} will be sent
                to {accessibleParticipants.length} participant(s), if not
                already assigned
              </Typography>
            </Box>
            <Box sx={ModalDrawerButtonStyles}>
              {!submitLoader ? (
                <>
                  <Button onClick={closeDrawer} variant="outlined">
                    Cancel
                  </Button>
                  <Button onClick={submitHandler} variant="contained">
                    Send {formType === "consent" ? "Consent" : "Form"}
                  </Button>
                </>
              ) : (
                <CircularProgress size={25} />
              )}
            </Box>
          </Box>
        </>
      </Box>
    </Box>
  );
};

export default SendMultipleConsentDrawer;
